import { h } from '@/components/dom';
import { segmented } from '@/components/controls';
import { icons, svgIcon } from '@/components/icons';
import type { App } from './App';
import type { EditTool, ViewMode } from '@/state/appState';

export interface StageChrome {
  /** Floating view switcher at the top of the workspace. */
  viewbar: HTMLElement;
  /** Floating tool rail on the left of the workspace. */
  rail: HTMLElement;
}

const TOOLS: Array<{ id: EditTool; label: string; icon: string }> = [
  { id: 'select', label: 'Select a string', icon: icons.select },
  { id: 'addString', label: 'Add a string between two pins', icon: icons.addString },
  { id: 'pan', label: 'Pan (or hold Shift)', icon: icons.hand },
];

export function createStageChrome(app: App, stage: HTMLElement): StageChrome {
  const view = segmented<ViewMode>({
    label: 'View',
    value: app.state.ui.view,
    options: [
      { value: 'strings', label: 'Strings', title: 'The strings placed so far' },
      { value: 'original', label: 'Original', title: 'The source image under the strings' },
      { value: 'processed', label: 'Target', title: 'The processed darkness map the optimizer works on' },
      { value: 'approximation', label: 'Live', title: "The engine's own approximation" },
      { value: 'difference', label: 'Difference', title: 'Signed difference between target and approximation' },
      { value: 'heatmap', label: 'Heat', title: 'Where the error is largest' },
    ],
    onChange: (v) => app.updateUi({ view: v }),
  });
  const renderer = segmented<'2d' | '3d'>({
    label: 'Renderer',
    value: app.state.ui.renderer,
    options: [
      { value: '2d', label: '2D', title: 'Canvas renderer with the physical thread model' },
      { value: '3d', label: '3D', title: 'Three.js presentation' },
    ],
    onChange: (v) => app.updateUi({ renderer: v }),
  });
  const viewbar = h('div', { class: 'viewbar' }, view.element, renderer.element);

  const toolButtons = TOOLS.map((t) => {
    const b = h('button', { type: 'button', class: 'rail__btn', dataset: { tool: t.id, tip: t.label }, attrs: { 'aria-label': t.label } }, svgIcon(t.icon));
    b.addEventListener('click', () => app.updateUi({ tool: t.id, pendingPin: null }));
    return b;
  });
  const zoom = (factor: number) => app.renderer?.zoomAt(factor, stage.clientWidth / 2, stage.clientHeight / 2);
  const railButton = (icon: string, label: string, onClick: () => void, sep = false) => {
    const b = h('button', { type: 'button', class: `rail__btn${sep ? ' rail__btn--sep' : ''}`, dataset: { tip: label }, attrs: { 'aria-label': label } }, svgIcon(icon));
    b.addEventListener('click', onClick);
    return b;
  };
  const zoomIn = railButton(icons.zoomIn, 'Zoom in', () => zoom(1.25), true);
  const zoomOut = railButton(icons.zoomOut, 'Zoom out', () => zoom(0.8));
  const fit = railButton(icons.fit, 'Fit the frame (double-click)', () => app.renderer?.resetView());
  const rail = h('div', { class: 'rail', attrs: { role: 'toolbar', 'aria-label': 'Tools' } }, ...toolButtons, zoomIn, zoomOut, fit);

  app.store.subscribe((s, p) => {
    if (s.ui !== p.ui || s === p) {
      view.setValue(s.ui.view);
      renderer.setValue(s.ui.renderer);
      for (const b of toolButtons) b.classList.toggle('is-on', b.dataset.tool === s.ui.tool);
      const three = s.ui.renderer === '3d';
      for (const b of [...toolButtons, zoomIn, zoomOut, fit]) b.disabled = three;
    }
  }, { immediate: true });

  return { viewbar, rail };
}
